import { FROG_X, GROUND, TICK_HZ } from '../sim/constants';
import type { Sim } from '../sim/sim';
import type { SceneId } from '../sim/types';
import { computeView, drawPad, drawPosts, ell, type View } from './common';
import { drawFrog, drawFrogShadow } from './frog';
import type { Fx } from './fx';
import { drawCranks, drawRope, drawTangledRope, ropeAt, type RopeState } from './rope';
import { SCENES } from './scenes';
import type { DrawCtx } from './scenes/types';
import { drawTarget } from './targets';

export interface RenderOptions {
  reduced: boolean;
  highContrast: boolean;
  rhythmRing: boolean;
  /** Extra rope lead in ticks, from the timing calibration. */
  leadTicks: number;
}

const TRANSITION_S = 1;

export class Renderer {
  readonly ctx: CanvasRenderingContext2D;
  view!: View;
  private scene: SceneId = 'pond';
  private prevScene: SceneId | null = null;
  private transT = TRANSITION_S;
  private gust = 0;

  constructor(readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D no disponible');
    this.ctx = ctx;
    this.resize();
  }

  resize(): void {
    const r = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.max(1, Math.round(r.width * dpr));
    this.canvas.height = Math.max(1, Math.round(r.height * dpr));
    this.view = computeView(r.width, r.height, dpr);
  }

  setScene(id: SceneId, instant = false): void {
    if (id === this.scene) return;
    this.prevScene = instant ? null : this.scene;
    this.scene = id;
    this.transT = instant ? TRANSITION_S : 0;
  }

  get currentScene(): SceneId {
    return this.scene;
  }

  /**
   * Draws one frame. `alpha` (0..1) is how far real time is past the last
   * simulation tick, used to smooth motion on high refresh-rate screens.
   */
  draw(sim: Sim, fx: Fx, opts: RenderOptions, t: number, dt: number, alpha: number): void {
    const { ctx, view: v } = this;
    this.transT = Math.min(TRANSITION_S, this.transT + dt);
    const target = sim.turn.gust * (sim.pending?.gust ? 1 : 0.4);
    this.gust += (target - this.gust) * Math.min(1, dt * 2);

    const art = SCENES[this.scene];
    const speed = sim.speedAt(sim.phase - Math.floor(sim.phase));
    const phase = sim.over ? sim.phase : sim.phase + speed * (alpha + opts.leadTicks);
    const frogY = sim.air ? Math.max(0, sim.y + sim.vy * (alpha / TICK_HZ)) : 0;
    const sinceCross = phase - Math.floor(phase);
    const beat = Math.max(0, 1 - sinceCross * 4);

    let sx = 0;
    let sy = 0;
    if (fx.shake > 0 && !opts.reduced) {
      sx = (Math.random() - 0.5) * 12 * fx.shake;
      sy = (Math.random() - 0.5) * 12 * fx.shake;
    }
    ctx.setTransform(v.dpr * v.s, 0, 0, v.dpr * v.s, v.dpr * (v.ox + sx), v.dpr * (v.oy + sy));

    const d: DrawCtx = {
      ctx, v, t, phase, beat, frogY, gust: this.gust, reduced: opts.reduced, hc: opts.highContrast, fx,
    };

    // background, with a watery wipe when the scene changes
    if (this.prevScene && this.transT < TRANSITION_S) {
      SCENES[this.prevScene].drawBack(d);
      const k = this.transT / TRANSITION_S;
      const edge = v.L - 60 + (v.R - v.L + 120) * k;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(v.L - 10, v.T - 10);
      for (let y = v.T - 10; y <= v.B + 10; y += 20) ctx.lineTo(edge + Math.sin(y * 0.05 + t * 6) * 18, y);
      ctx.lineTo(v.L - 10, v.B + 10);
      ctx.closePath();
      ctx.clip();
      art.drawBack(d);
      ctx.restore();
    } else {
      art.drawBack(d);
    }

    drawPosts(ctx, art.ground);
    drawPad(ctx, art.ground, fx.ripples);

    const tangled = fx.tangleT >= 0;
    const fatal = tangled && sim.over && !sim.won;

    if (opts.rhythmRing && !fatal) this.drawRing(sim, phase, art.ring);

    const ropes: RopeState[] = [];
    if (!fatal) {
      ropes.push(ropeAt(phase, sim.turn.dir, false));
      if (sim.turn.double) ropes.push(ropeAt(phase, sim.turn.dir, true));
    }
    const frogX = FROG_X + fx.slideX;
    drawFrogShadow(ctx, frogX, frogY);
    for (const r of ropes) if (r.z < 0) drawRope(ctx, r, art.rope, art.ropeBack, opts.highContrast);
    if (fatal) drawTangledRope(ctx, art.rope, art.ropeBack, opts.highContrast, frogX);
    drawCranks(ctx, ropes.length ? ropes : [ropeAt(0, 1, false)], art.handle);

    const ropeMidY = ropes.length ? ropes[0].midY : GROUND;
    drawFrog(ctx, {
      x: frogX,
      y: frogY,
      air: sim.air,
      stretch: sim.air && sim.vy > 0 ? Math.min(1, sim.vy / 400) : 0,
      squash: fx.squash,
      croakT: fx.croakT,
      tangleT: tangled ? fx.tangleT : -1,
      bubble: sim.bubble,
      lookDY: (ropeMidY - (GROUND - frogY - 60)) / 90,
      rope: art.rope,
      ropeBack: art.ropeBack,
      hc: opts.highContrast,
      reduced: opts.reduced,
      cold: this.scene === 'ice',
    });
    for (const r of ropes) if (r.z >= 0) drawRope(ctx, r, art.rope, art.ropeBack, opts.highContrast);

    for (const tg of sim.targets) drawTarget(ctx, tg, t, opts.reduced);
    this.drawTongue(fx, frogX, frogY);
    fx.draw(ctx);
    art.drawFront(d);

    if (fx.flash > 0) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = Math.min(opts.reduced ? 0.25 : 0.7, fx.flash);
      ctx.fillStyle = fx.flashColor;
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.restore();
    }
  }

  /** A ring that closes in on the frog and meets it when the rope passes. */
  private drawRing(sim: Sim, phase: number, color: string): void {
    const { ctx } = this;
    const frac = phase - Math.floor(phase);
    const p = sim.turn.double ? (frac * 2) % 1 : frac;
    const k = 1 - p;
    const rx = 48 + 110 * k;
    ell(ctx, FROG_X, GROUND + 4, rx, rx * 0.17);
    ctx.globalAlpha = 0.25 + 0.6 * p;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2 + 2 * p;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  private drawTongue(fx: Fx, frogX: number, frogY: number): void {
    if (!fx.tongue) return;
    const { ctx } = this;
    const k = fx.tongue.t / 0.18;
    const out = k < 0.5 ? k * 2 : (1 - k) * 2;
    const mx = frogX;
    const my = GROUND - frogY - 44;
    const tx = fx.tongue.x;
    const ty = GROUND - fx.tongue.h;
    const ex = mx + (tx - mx) * out;
    const ey = my + (ty - my) * out;
    ctx.strokeStyle = '#E86A8A';
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(mx, my);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    ell(ctx, ex, ey, 5, 5);
    ctx.fillStyle = '#F08AA4';
    ctx.fill();
  }
}
